import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { LucidePhone } from '@lucide/angular';
import { AuthService } from '../../auth/auth.service';
import { EmergencyContactService } from '../services/emergency-contact.service';
import type { ContactDto } from '../../auth/models/auth.models';

@Component({
  selector: 'app-contact-fab',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet],
  template: `
    @if (visible()) {
      <div class="contact-fab">
        <button
          type="button"
          class="contact-fab__btn"
          aria-label="Kontak Call Center"
          (click)="svc.open()"
        >
          <ng-container [ngComponentOutlet]="phoneIcon" [ngComponentOutletInputs]="{ size: 24, strokeWidth: 2 }" />
        </button>
      </div>
    }
  `,
  styles: `
    .contact-fab {
      position: fixed;
      bottom: 24px;
      right: 24px;
      z-index: 9999;
    }

    .contact-fab__btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 56px;
      height: 56px;
      border-radius: 9999px;
      border: none;
      cursor: pointer;
      color: #fff;
      background: #20335D;
      box-shadow: 0 4px 14px rgba(32, 51, 93, 0.45);
      transition: transform 0.15s ease, box-shadow 0.15s ease;

      &:hover {
        transform: scale(1.08);
        box-shadow: 0 6px 20px rgba(32, 51, 93, 0.55);
      }
    }

    @media (max-width: 480px) {
      .contact-fab { bottom: 16px; right: 16px; }
    }
  `,
})
export class ContactFabComponent {
  protected readonly svc = inject(EmergencyContactService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  protected readonly phoneIcon = LucidePhone;

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  protected readonly visible = computed(() => {
    if (this.url().startsWith('/admin')) return false;
    const contacts = this.authService.currentUser()?.institution?.contacts ?? [];
    return contacts.length > 0;
  });
}
